import * as pdfjsLib from "pdfjs-dist";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { uploadAndSendPdf } from "../../services/FileService";
import { Toast } from "primereact/toast";
import { AxiosResponse } from "axios";

(pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${
  (pdfjsLib as any).version
}/pdf.worker.min.js`;

export interface PdfPreviewProps {
  pdfDefinition: any;
  documentName?: any;
  downloadDocument?: any; // očekuješ ref spolja
  printDocument?: any; // očekuješ ref spolja
  sendDocument?: any; // očekuješ ref spolja
  registrationId?: string;
}

export const PdfPreview = (p: PdfPreviewProps) => {
  const pdfDefinition = p.pdfDefinition;

  // INTERNAL STATE/REFS
  const queueLen = useRef<number>(0);
  const generating = useRef<boolean>(false);
  const currentPdfRef = useRef<any>(null);
  const loadingTaskRef = useRef<any>(null);
  const renderTasksRef = useRef<any[]>([]);
  const cancelTokenRef = useRef(0);
  const mountedRef = useRef<boolean>(true);

  const toast = useRef<any>(null);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const pdfHostRef = useRef<HTMLDivElement | null>(null);
  const [isRendering, setIsRendering] = useState(false);

  // Helper: normalizuj buffer u Uint8Array za pdf.js
  const toTypedArray = (buf: any): Uint8Array => {
    if (buf instanceof Uint8Array) return buf;
    if (buf instanceof ArrayBuffer) return new Uint8Array(buf);
    if (buf?.buffer instanceof ArrayBuffer) return new Uint8Array(buf.buffer);
    return new Uint8Array();
  };

  // Ekspozicija metoda kroz ref-ove
  useEffect(() => {
    if (p.downloadDocument) p.downloadDocument.current = downloadDocument;
    if (p.printDocument) p.printDocument.current = printDocument;
    if (p.sendDocument) p.sendDocument.current = sendPdfFunction;
  }, [
    p.downloadDocument,
    p.printDocument,
    p.sendDocument,
    p.pdfDefinition,
    p.documentName,
    p.registrationId,
  ]);

  // (Re)generiši prikaz kada se promeni definicija
  useEffect(() => {
    mountedRef.current = true;
    if (pdfDefinition && pdfContainerRef.current) {
      generatePdf();
    }
    return () => {
      mountedRef.current = false;
      // signal cancel to running tasks
      cancelTokenRef.current++;
      try {
        renderTasksRef.current.forEach((rt) => rt?.cancel?.());
      } catch {}
      renderTasksRef.current = [];
      try {
        loadingTaskRef.current?.destroy?.();
      } catch {}
      queueLen.current = 0;
      generating.current = false;
      setIsRendering(false);
    };
  }, [pdfDefinition]);

  // JAVNE AKCIJE — zadržavamo ista imena ponašanja
  const downloadDocument = () => {
    const doc = pdfMake.createPdf(cloneDeep(pdfDefinition));
    const fileName = p.documentName ? String(p.documentName) : "profaktura.pdf";
    doc.download(fileName);
  };

  const printDocument = () => {
    const doc = pdfMake.createPdf(cloneDeep(pdfDefinition));
    doc.print();
  };

  const sendPdfFunction = () => {
    const pdfDocGenerator = pdfMake.createPdf(cloneDeep(pdfDefinition));
    pdfDocGenerator.getBuffer((buffer: ArrayBuffer | Uint8Array | any) => {
      const blob = new Blob([buffer as any], { type: "application/pdf" });
      uploadAndSendPdf(blob, p.registrationId)
        .then((res: AxiosResponse) => {
          toast?.current?.show({
            severity: "success",
            detail: `${res?.data?.message ?? "PDF poslat"}`,
            life: 3000,
          });
        })
        .catch((err: any) => {
          toast?.current?.show({
            severity: "error",
            detail: err?.message || "Greška pri slanju PDF-a",
            life: 4000,
          });
        });
    });
  };

  // GLAVNA RED/QUEUE LOGIKA — stabilna bez setTimeout
  const generatePdf = () => {
    queueLen.current++;
    if (generating.current) return; // već u toku

    generating.current = true;
    setIsRendering(true);

    currentPdfRef.current = pdfMake.createPdf(cloneDeep(pdfDefinition));
    currentPdfRef.current.getBuffer(async (buff: ArrayBuffer | Uint8Array) => {
      try {
        await onPdfGenerated(buff);
      } catch (e: any) {
        console.error(e);
        toast?.current?.show({
          severity: "error",
          detail: e?.message || "Greška pri prikazu PDF-a",
        });
      } finally {
        queueLen.current--;
        generating.current = false;
        setIsRendering(false);
        if (queueLen.current > 0) generatePdf();
      }
    });
  };

  const onPdfGenerated = async (buff: ArrayBuffer | Uint8Array) => {
    if (!buff) return;

    // clear only our inner host; leave React container alone
    if (pdfHostRef.current) pdfHostRef.current.textContent = "";

    const myToken = ++cancelTokenRef.current;
    const data = toTypedArray(buff);
    const loadingTask = pdfjsLib.getDocument({ data });
    loadingTaskRef.current = loadingTask;

    let pdf: any;
    try {
      pdf = await loadingTask.promise;
    } catch (e: any) {
      if (String(e?.message || e).includes("Worker was destroyed")) return; // benign on cancel
      throw e;
    }

    for (let i = 1; i <= pdf.numPages; i++) {
      if (myToken !== cancelTokenRef.current) return; // canceled
      const page = await pdf.getPage(i);
      await printPdfPageOnCanvas(page, myToken);
    }
  };

  const printPdfPageOnCanvas = async (page: any, token?: number) => {
    const container = pdfHostRef.current;
    if (!container || !mountedRef.current) return;

    const dpr = window.devicePixelRatio || 1;
    const containerWidth = Math.max(720, container.clientWidth);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(2, containerWidth / baseViewport.width);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (token && token !== cancelTokenRef.current) return;

    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.style.display = "block";
    canvas.style.margin = "0 auto 16px auto";
    canvas.style.border = "1px solid #e5e7eb";

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (!mountedRef.current || !pdfContainerRef.current?.isConnected) return;
    pdfHostRef.current?.appendChild(canvas);

    const renderTask = page.render({ canvasContext: ctx, viewport });
    renderTasksRef.current.push(renderTask);
    try {
      await renderTask.promise;
    } catch (e) {
      // render cancelled — ignore
    }
  };

  const loadingLabel = useMemo(
    () => (isRendering ? "PDF se učitava…" : undefined),
    [isRendering]
  );

  return (
    <>
      <Toast ref={toast} position={"top-right"} />
      <div style={{ width: "100%", height: "100%", marginLeft: "0px" }}>
        <div
          ref={pdfContainerRef}
          style={{
            padding: "0px",
            width: "100%",
            overflow: "auto",
            margin: "0 auto",
          }}
          aria-busy={isRendering}
          aria-live="polite"
        >
          {loadingLabel && (
            <div
              style={{ padding: "12px", textAlign: "center", color: "#6b7280" }}
            >
              {loadingLabel}
            </div>
          )}
          <div ref={pdfHostRef} />
        </div>
      </div>
    </>
  );
};
